import { API_CONFIG } from '../config/api.ts';

/**
 * Handles unauthorized (401) errors by redirecting to login page
 */
const handleUnauthorized = () => {
  // Clear any stored session data
  localStorage.removeItem('sessionToken');
  
  // Redirect to login page
  window.location.href = '/login';
};

/**
 * Generic API request function with 401 error handling
 * This function checks for 401 (Unauthorized) responses and redirects to login
 */
export const apiRequest = async <T>(
  url: string,
  options: RequestInit = {}
): Promise<T> => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), API_CONFIG.TIMEOUT);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    // Check for 401 Unauthorized
    if (response.status === 401) {
      handleUnauthorized();
      throw new Error('Unauthorized - Please login again');
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        message: 'An error occurred',
        statusCode: response.status,
      }));
      throw new Error(errorData.message || `HTTP ${response.status}`);
    }

    if (response.status === 204 || response.headers.get('content-length') === '0') {
      return undefined as T;
    }

    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      return undefined as T;
    }

    return await response.json();
  } catch (error) {
    clearTimeout(timeoutId);
    if (error instanceof Error) {
      if (error.name === 'AbortError') {
        throw new Error('Request timeout');
      }
      // If it's already our unauthorized error, re-throw it
      if (error.message === 'Unauthorized - Please login again') {
        throw error;
      }
      throw error;
    }
    throw new Error('An unexpected error occurred');
  }
};

